import React, { useEffect, useState, useRef } from 'react';
import { X, Download, Printer, Loader } from 'lucide-react';
import { Btn } from '../ui';

const mimeFromFileName = (name = '') => {
  const ext = name.split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'application/pdf';
  if (['jpg', 'jpeg'].includes(ext)) return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  return 'application/octet-stream';
};

const HRDocViewer = ({ fileName, mimeType, fetchBlob, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    let url;
    fetchBlob()
      .then(({ data }) => {
        url = URL.createObjectURL(data);
        setBlobUrl(url);
      })
      .catch(() => setError(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const mime = mimeType || mimeFromFileName(fileName);
  const isPdf = mime === 'application/pdf';

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.click();
  };

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-xl shadow-xl flex flex-col w-full max-w-6xl h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-medium text-gray-700 truncate">{fileName}</h2>
          <div className="flex items-center gap-2">
            {blobUrl && (
              <>
                <Btn size="sm" onClick={handleDownload} title="Télécharger">
                  <Download size={14} /> Télécharger
                </Btn>
                {isPdf && (
                  <Btn size="sm" variant="secondary" onClick={handlePrint} title="Imprimer">
                    <Printer size={14} /> Imprimer
                  </Btn>
                )}
              </>
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600"
              title="Fermer"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 bg-gray-100 overflow-hidden">
          {error ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              Impossible de charger le fichier.
            </div>
          ) : !blobUrl ? (
            <div className="flex items-center justify-center h-full">
              <Loader className="animate-spin text-primary" size={28} />
            </div>
          ) : isPdf ? (
            <iframe
              ref={iframeRef}
              src={blobUrl}
              title={fileName}
              className="w-full h-full border-0"
            />
          ) : (
            <div className="flex items-center justify-center h-full p-6">
              <img
                src={blobUrl}
                alt={fileName}
                className="max-w-full max-h-full object-contain rounded-lg shadow"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HRDocViewer;
